import { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2, FiClock, FiCalendar } from 'react-icons/fi';
import { schedulesApi } from '../services/api';

// Timezone options
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'UTC', label: 'UTC' },
];

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday', 
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

export default function AgentScheduleModal({ agent, onClose, onSave, portalId }) {
  const [timezone, setTimezone] = useState('America/New_York');
  const [dailySchedules, setDailySchedules] = useState({});
  const [timeOffPeriods, setTimeOffPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableAllDays, setAvailableAllDays] = useState({
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false
  });

  // Load existing schedule data when component mounts
  useEffect(() => {
    const loadScheduleData = async () => {
      if (!agent?.id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Try to load existing schedule
        console.log("Fetching schedule data for agent:", agent.id);
        const scheduleData = await schedulesApi.fetchAgentSchedule(agent.id, portalId);
        
        if (scheduleData) {
          setTimezone(scheduleData.timezone || 'America/New_York');
          const loadedSchedules = scheduleData.dailySchedules || getDefaultSchedules();
          setDailySchedules(loadedSchedules);
          setTimeOffPeriods(scheduleData.timeOffPeriods || []);

          // Detect days already marked all-day (covers all known formats)
          const allDayState = {};
          const ALL_DAY_START = ['00:00', '00:00:00'];
          const ALL_DAY_END = ['23:59:59', '11:59', '11:59:00'];
          DAYS_OF_WEEK.forEach(day => {
            allDayState[day.toLowerCase()] = (loadedSchedules[day] || []).some(
              block => ALL_DAY_START.includes(block.startTime) && ALL_DAY_END.includes(block.endTime)
            );
          });
          setAvailableAllDays(allDayState);
        } else {
          setDailySchedules(getDefaultSchedules());
        }
      } catch (error) {
        console.error('Error loading schedule data:', error);
        setError('Failed to load existing schedule. Please close and retry before saving.');
      } finally {
        setLoading(false);
      }
    };

    loadScheduleData();
  }, [agent?.id, portalId]);

  // Get default schedules (Monday-Friday 9-5)
  const getDefaultSchedules = () => {
    const defaultSchedules = {};
    DAYS_OF_WEEK.forEach((day, index) => {
      if (index >= 1 && index <= 5) {
        defaultSchedules[day] = [{ startTime: '09:00', endTime: '17:00' }];
      } else {
        defaultSchedules[day] = [];
      }
    });
    return defaultSchedules;
  };

  const handleAddTimeBlock = (day) => {
    setDailySchedules(prev => ({
      ...prev,
      [day]: [...(prev[day] || []), { startTime: '09:00', endTime: '17:00' }]
    }));
  };

  const handleRemoveTimeBlock = (day, index) => {
    setDailySchedules(prev => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== index)
    }));
  };

  const handleTimeBlockChange = (day, index, field, value) => {
    setDailySchedules(prev => ({
      ...prev,
      [day]: prev[day].map((block, i) => 
        i === index ? { ...block, [field]: value } : block
      )
    }));
  };

  const allDaysMarked = DAYS_OF_WEEK.every(day => availableAllDays[day.toLowerCase()]);

  const markAllAvailableAllDay = () => {
    if (allDaysMarked) {
      const schedules = {};
      const state = {};
      DAYS_OF_WEEK.forEach(day => {
        schedules[day] = [{ startTime: '09:00', endTime: '17:00' }];
        state[day.toLowerCase()] = false;
      });
      setDailySchedules(prev => ({ ...prev, ...schedules }));
      setAvailableAllDays(prev => ({ ...prev, ...state }));
    } else {
      const schedules = {};
      const state = {};
      DAYS_OF_WEEK.forEach(day => {
        schedules[day] = [{ startTime: '00:00:00', endTime: '23:59:59' }];
        state[day.toLowerCase()] = true;
      });
      setDailySchedules(prev => ({ ...prev, ...schedules }));
      setAvailableAllDays(prev => ({ ...prev, ...state }));
    }
  };

  // const markAvailableAllDay = (day) => {
  //   const dayKey = day.toLowerCase();
  //   const isCurrentlyAllDay = availableAllDays[dayKey];

  //   if (isCurrentlyAllDay) {
  //     // Toggle off: restore default single block
  //     setDailySchedules(prev => ({
  //       ...prev,
  //       [day]: [{ startTime: '09:00', endTime: '17:00' }]
  //     }));
  //     setAvailableAllDays(prev => ({ ...prev, [dayKey]: false }));
  //   } else {
  //     // Toggle on: replace all blocks with a single 00:00:00–23:59:59 block
  //     setDailySchedules(prev => ({
  //       ...prev,
  //       [day]: [{ startTime: '00:00:00', endTime: '23:59:59' }]
  //     }));
  //     setAvailableAllDays(prev => ({ ...prev, [dayKey]: true }));
  //   }
  // }

  const handleAddTimeOff = () => {
    const today = new Date().toISOString().split('T')[0];
    setTimeOffPeriods(prev => [...prev, {
      startDate: today,
      endDate: today,
      reason: '',
      type: 'vacation'
    }]);
  };

  const handleRemoveTimeOff = (id) => {
    setTimeOffPeriods(prev => prev.filter(period => period.id !== id));
  };

  const handleTimeOffChange = (id, field, value) => {
    setTimeOffPeriods(prev => prev.map(period =>
      period.id === id ? { ...period, [field]: value } : period
    ));
  };

  const handleSave = () => {
    if (loading || error) return;

    const scheduleData = {
      timezone,
      dailySchedules,
      timeOffPeriods
    };
    onSave({ ...scheduleData, hs_portal_id: portalId });
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Manage Schedule for {agent.firstName} {agent.lastName}
              </h2>
              <p className="text-sm text-gray-600 mt-1">{agent.email}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Loading schedule...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Content */}
          {!loading && (
            <>
              {/* Timezone Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {TIMEZONES.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Daily Schedules */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <FiClock className="w-5 h-5 text-gray-600 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">Daily Schedules</h3>
                  </div>
                  <button
                    onClick={markAllAvailableAllDay}
                    className={`text-sm px-3 py-1 rounded border transition-colors ${
                      allDaysMarked
                        ? 'bg-blue-100 text-blue-700 border-blue-400 hover:bg-blue-200'
                        : 'text-blue-600 border-blue-400 hover:bg-blue-50'
                    }`}
                  >
                    {allDaysMarked ? 'Available 24/7 (undo)' : 'Mark All Available 24/7'}
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Set available hours for each day of the week. You can add multiple time blocks per day to account for breaks.
                </p>

                <div className="space-y-6">
                  {DAYS_OF_WEEK.map(day => {
                    const dayKey = day.toLowerCase();
                    const isAllDay = availableAllDays[dayKey];
                    return (
                    <div key={day} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{day}</h4>
                        <button
                          onClick={() => handleAddTimeBlock(day)}
                          disabled={isAllDay}
                          className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <FiPlus className="w-4 h-4 mr-1" />
                          Add Time Block
                        </button>
                      </div>

                      {dailySchedules[day]?.length > 0 ? (
                        <div className="space-y-2">
                          {dailySchedules[day].map((block, index) => (
                            <div key={index} className="flex items-center space-x-3 bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="time"
                                  value={block.startTime}
                                  disabled={isAllDay}
                                  onChange={(e) => handleTimeBlockChange(day, index, 'startTime', e.target.value)}
                                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-40 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                />
                                <span className="text-gray-500">to</span>
                                <input
                                  type="time"
                                  value={block.endTime}
                                  disabled={isAllDay}
                                  onChange={(e) => handleTimeBlockChange(day, index, 'endTime', e.target.value)}
                                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-40 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                />
                              </div>
                              <div className="text-sm text-gray-600">
                                {isAllDay ? 'All Day' : `(${formatTime(block.startTime)} - ${formatTime(block.endTime)})`}
                              </div>
                              <button
                                onClick={() => handleRemoveTimeBlock(day, index)}
                                disabled={isAllDay}
                                className="text-red-600 hover:text-red-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No available hours set for this day</p>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Time Off Periods */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <FiCalendar className="w-5 h-5 text-gray-600 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">Time Off</h3>
                  </div>
                  <button
                    onClick={handleAddTimeOff}
                    className="flex items-center text-sm bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FiPlus className="w-4 h-4 mr-1" />
                    Add Time Off
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Block out periods when the agent is unavailable (vacation, leaves, etc.)
                </p>

                {timeOffPeriods.length > 0 ? (
                  <div className="space-y-4">
                    {timeOffPeriods.map(period => (
                      <div key={period.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start space-x-4">
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date
                              </label>
                              <input
                                type="date"
                                value={period.startDate}
                                onChange={(e) => handleTimeOffChange(period.id, 'startDate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date
                              </label>
                              <input
                                type="date"
                                value={period.endDate}
                                onChange={(e) => handleTimeOffChange(period.id, 'endDate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveTimeOff(period.id)}
                            className="text-red-600 hover:text-red-800 transition-colors mt-6"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Type
                            </label>
                            <select
                              value={period.type || 'vacation'}
                              onChange={(e) => handleTimeOffChange(period.id, 'type', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="vacation">Vacation</option>
                              <option value="sick">Sick Leave</option>
                              <option value="personal">Personal</option>
                              <option value="holiday">Holiday</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Reason (optional)
                            </label>
                            <input
                              type="text"
                              value={period.reason || ''}
                              onChange={(e) => handleTimeOffChange(period.id, 'reason', e.target.value)}
                              placeholder="e.g., Family vacation, Conference"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No time off periods set</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-xl">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !!error}
              title={loading ? 'Schedule is still loading' : (error ? 'Resolve loading error before saving' : 'Save Schedule')}
              className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              Save Schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
